'use client';

import React, { useEffect } from 'react';
import { Icon } from '../Icon';
import { Typography } from '../Typography/Typography';
import { useDropdown } from '@workspace/ui/common/Filter/useDropdownFilter';
import { getLabel, isDisabled } from '@/app/lib/common/DropdownFilter/dropdownFilter.util';
import { DropdownOption } from '../Dropdowns';
import { IconMap } from '../IconMap';
import { CustomScrollBar } from '../CustomScrollBar';
import { cn } from '@workspace/lib';

export type FilterProps = {
  placeholder: string;
  startIcon?: keyof typeof IconMap | '';
  icon: keyof typeof IconMap;
  endIcon?: keyof typeof IconMap | '';
  options: DropdownOption[];
  onSelect?: (value: string) => void;
  value?: string;
  syncExternalValue?: boolean;
  className?: string;
  iconClassName?: string;
  optionClassName?: string;
  optionIconClassName?: string;
  iconSize?: number;
  emptyState?: React.ReactNode;
};

const DefaultEmptyState = (
  <div className="p-4">
    <Typography variant="bodyMd" className="text-center">
      No options available
    </Typography>
  </div>
);

export function Filter({
  placeholder,
  startIcon,
  icon,
  endIcon,
  options,
  onSelect,
  value,
  syncExternalValue,
  className,
  iconClassName,
  optionClassName,
  optionIconClassName,
  iconSize = 16,
  emptyState = DefaultEmptyState,
}: FilterProps) {
  const { isOpen, selected, toggle, select, setSelected, rootRef, listRef } = useDropdown({
    onSelect: (o) => onSelect?.(o.value),
  });

  // Sync external value with internal selected state
  useEffect(() => {
    if (!syncExternalValue) return;
    if (value) {
      const selectedOption = options.find((option) => option.value === value);
      if (selectedOption && selected?.value !== value) {
        setSelected(selectedOption);
      }
    } else if (selected && !value) {
      setSelected(null);
    }
  }, [value, options, selected, setSelected, syncExternalValue]);

  return (
    <div ref={rootRef} className={cn('relative w-full max-w-50', className)}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={toggle}
        className={cn(
          'flex h-12 w-full items-center gap-2 rounded-[5px] border border-secondary-100 bg-white px-3 cursor-pointer',
        )}
      >
        <Typography variant="bodyMd" className={cn('font-openSans text-secondary-200 line-clamp-1')}>
          {getLabel(selected, placeholder)}
        </Typography>
        <Icon name={icon} size={iconSize} className={cn('ml-auto', iconClassName)} />
      </button>

      {isOpen && (
        <div className={cn('absolute z-10 mt-2 w-full rounded-[1px] border border-secondary-100 bg-white shadow-lg')}>
          {options.length > 0 ? (
            <CustomScrollBar
              externalScrollRef={listRef as unknown as React.RefObject<HTMLDivElement>}
              maxHeight="max-h-60"
              className="w-full"
              trackInsetTopPx={10}
              trackInsetBottomPx={10}
              contentClassName="py-1 pr-1"
              trackClassName="right-0"
              thumbWidth="w-[3px]"
              thumbHeight="!h-22"
              thumbClassName="bg-primary"
              autoHide={false}
            >
              <ul role="listbox" tabIndex={0} aria-label={placeholder} className="w-full">
                {options.map((o) => (
                  <li
                    key={o.id}
                    className={cn(
                      'border-b border-accent last:border-0 hover:border-b hover:border-primary',
                      selected?.value === o.value ? 'bg-secondary-50' : '',
                      isDisabled(o) && 'border-b bg-secondary-50 hover:border-none pointer-events-none',
                    )}
                  >
                    <div className="flex items-center gap-2 py-2 px-4">
                      {startIcon && <Icon name={startIcon} size={iconSize} className={cn(optionIconClassName)} />}
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected?.value === o.value}
                        disabled={isDisabled(o)}
                        onClick={() => !isDisabled(o) && select(o)}
                        className={cn('flex w-full items-center gap-2 text-left', optionClassName)}
                      >
                        <Typography variant="bodySm" className="text-gray-700">
                          {o.label}
                        </Typography>
                      </button>
                      {endIcon && <Icon name={endIcon} size={iconSize} className={cn(optionIconClassName)} />}
                    </div>
                  </li>
                ))}
              </ul>
            </CustomScrollBar>
          ) : (
            <>{emptyState}</>
          )}
        </div>
      )}
    </div>
  );
}

export default Filter;
